"use client";

import Image from "next/image";
import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { cn, renderRichText } from "@/lib/utils";
import { type ColorScheme, COLOR_SCHEMES } from "@/lib/colorSchemes";

export interface CardItem {
  title: string;
  description?: string;
  /** Desktop image/SVG rendered at the bottom of the active card. */
  imageSrc?: string;
  /** Mobile image/SVG — falls back to imageSrc if not provided. */
  imageMobileSrc?: string;
  /** Color scheme for this card. Defaults to "steel-blue". */
  colorScheme?: ColorScheme;
}

export interface CardDeckProps {
  variant?: "bento";
  /** 0-based index of the card that starts expanded/active. Defaults to 0. */
  defaultActiveIndex?: number;
  cards: CardItem[];
  /** Default color scheme applied to all cards. Per-card colorScheme overrides this. */
  colorScheme?: ColorScheme;
}

export function CardDeck({
  variant = "bento",
  defaultActiveIndex = 0,
  cards,
  colorScheme: deckScheme = "steel-blue",
}: CardDeckProps) {
  const [activeIndex, setActiveIndex] = useState(defaultActiveIndex);

  return (
    <>
      {/* Mobile: vertical accordion — height driven by content */}
      <div className="flex flex-col gap-2 md:hidden">
        {cards.map((card, index) => {
          const isActive = index === activeIndex;
          const scheme = COLOR_SCHEMES[card.colorScheme ?? deckScheme];
          const stateOverride = isActive ? scheme.active : scheme.inactive;
          const bg = stateOverride?.bg ?? scheme.bg;
          const body = stateOverride?.body ?? scheme.body;
          const bodyMuted = stateOverride?.bodyMuted ?? scheme.bodyMuted;
          return (
            <div
              key={index}
              className={cn(
                "rounded-2xl overflow-hidden cursor-pointer",
                bg,
                body,
              )}
              onClick={() => setActiveIndex(index)}
            >
              <div className="flex items-center justify-between px-5 py-4">
                <span className={cn("font-semibold")}>{card.title}</span>
                <ChevronDown
                  className={cn(
                    "transition-transform duration-300",
                    isActive && "rotate-180",
                  )}
                  size={20}
                  aria-hidden
                />
              </div>
              {isActive && (
                <div>
                  {card.description && (
                    <p className={cn("px-5 text-body-md mb-8", bodyMuted)}>
                      {renderRichText(card.description)}
                    </p>
                  )}
                  {/* Mobile image — uses imageMobileSrc, falls back to imageSrc */}
                  {(card.imageMobileSrc ?? card.imageSrc) && (
                    <div className="w-full flex justify-center mt-4">
                      <Image
                        src={(card.imageMobileSrc ?? card.imageSrc)!}
                        alt=""
                        aria-hidden
                        loading="eager"
                        width={600}
                        height={400}
                        className="w-full h-auto"
                        sizes="100vw"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Desktop: 12-column inner grid (16px gutters)
          Expanded card = col-span-6, Collapsed card = col-span-3 */}
      <div
        className="hidden w-full md:grid grid-cols-12 gap-4"
        style={{
          gridTemplateColumns: cards
            .map((_, i) => (i === activeIndex ? "6fr" : "3fr"))
            .join(" "),
          transition: "grid-template-columns 300ms ease",
        }}
      >
        {cards.map((card, index) => {
          const isActive = index === activeIndex;
          const scheme = COLOR_SCHEMES[card.colorScheme ?? deckScheme];
          const stateOverride = isActive ? scheme.active : scheme.inactive;
          const bg = stateOverride?.bg ?? scheme.bg;
          const body = stateOverride?.body ?? scheme.body;
          const bodyMuted = stateOverride?.bodyMuted ?? scheme.bodyMuted;
          return (
            <div
              key={index}
              className={cn(
                "rounded-2xl overflow-hidden cursor-pointer",
                bg,
                body,
                "flex flex-col h-[28.2rem]",
              )}
              onClick={() => setActiveIndex(index)}
            >
              {/* Header — padded */}
              <div className="flex items-start justify-between p-6">
                <span className="text-heading-sm font-semibold">
                  {card.title}
                </span>
                <ChevronRight
                  className={cn(
                    "mt-[0.725rem] shrink-0 transition-transform duration-300",
                    isActive && "rotate-180",
                  )}
                  size={20}
                  aria-hidden
                />
              </div>

              {/* Content — only shown when active */}
              {isActive && (
                <div className="flex flex-col flex-1">
                  {/* Copy — padded */}
                  {card.description && (
                    <p className={cn("px-5 text-body-md mb-4", bodyMuted)}>
                      {renderRichText(card.description)}
                    </p>
                  )}
                  {/* Desktop image — uses imageSrc, pinned to bottom-center */}
                  {card.imageSrc && (
                    <div className="mt-auto w-full flex justify-center overflow-hidden">
                      <Image
                        src={card.imageSrc}
                        alt=""
                        aria-hidden
                        loading="eager"
                        width={600}
                        height={400}
                        className="w-full h-auto"
                        sizes="60rem"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

export default CardDeck;
